// src/Api/UsersApi.js
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig';
import CompanyUtils from '../Utils/CompanyUtils';
import { sendPasswordResetEmail as firebaseSendResetEmail } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { authFetch } from "../Utils/authFetch";

const API_BASE_URL = process.env.REACT_APP_API_URL;

/* ---------- USERS ---------- */

export const fetchUsers = async (companyId, { etag } = {}) => {
  try {
    const headers = {};
    if (etag) headers["If-None-Match"] = etag;

    const url = `${API_BASE_URL}/fetchUsers?companyId=${encodeURIComponent(companyId)}`;
    const response = await authFetch(url, { method: "GET", headers });

    if (response.status === 304) {
      return { status: 304 };
    }
    if (!response.ok) throw new Error("Failed to fetch users");

    const body = await response.json();
    const newEtag = response.headers.get("ETag");

    return {
      status: 200,
      users: body.users || [],
      version: body.version || 0,
      updatedAt: body.updatedAt || null,
      etag: newEtag,
    };
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

export const fetchUserById = async (userId) => {
  try {
    const response = await authFetch(
      `${API_BASE_URL}/fetchUserById?userId=${encodeURIComponent(userId)}`,
      { method: "GET" }
    );
    if (!response.ok) throw new Error("Käyttäjän haku epäonnistui");
    return await response.json();
  } catch (error) {
    console.error("Virhe käyttäjätiedon haussa:", error);
    return null;
  }
};

export const uploadProfileImage = async (userId, file) => {
  const resizedImage = await CompanyUtils.getResizedImage(file, 200, 200, 0.90);
  const imageRef = ref(storage, `profileImages/${userId}`);
  await uploadBytes(imageRef, resizedImage);
  const imageUrl = await getDownloadURL(imageRef);
  return imageUrl;
};

export const updateProfileImageUrl = async (userId, imageUrl) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/updateProfileImageUrl`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, imageUrl }),
    });
    if (!response.ok) {
      throw new Error("Profiilikuvan URL:n tallennus epäonnistui.");
    }
    return await response.json();
  } catch (error) {
    console.error("Virhe profiilikuvan url tallennuksessa:", error);
    return null;
  }
};

export const updateUserConsent = async (userId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/updateUserConsent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      throw new Error("Virhe suostumuksen päivittämisessä");
    }
    return await response.json();
  } catch (error) {
    console.error("Virhe suostumuksen päivittämisessä:", error);
    return null;
  }
};

export const updateLastSigned = async (userId) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/updateLastSigned`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    if (!response.ok) {
      throw new Error("Virhe kirjautumisajan päivittämisessä");
    }
    return await response.json();
  } catch (error) {
    console.error("Virhe kirjautumisajan päivittämisessä:", error);
    return null;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/updateUser`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, userData }),
    });
    if (!response.ok) throw new Error("Failed to update user");
    return await response.json();
  } catch (error) {
    console.error("Error updating user:", error);
    return null;
  }
};

export const updateUserRole = async (userId, companyId, role, hasRole) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/updateUserRole`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, companyId, role, hasRole }),
    });
    if (!response.ok) throw new Error("Failed to update role");
    return await response.json();
  } catch (error) {
    console.error("Error updating role:", error);
    return null;
  }
};

const updateUserRoles = async (userId, companyId, rolesArray) => {
  const response = await authFetch(`${API_BASE_URL}/updateUserRoles`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, companyId, roles: rolesArray }),
  });
  if (!response.ok) {
    throw new Error("Roolien päivitys epäonnistui.");
  }
  return await response.json();
};

export const updateUserActive = async (userId, isActive) => {
  try {
    const response = await authFetch(`${API_BASE_URL}/updateUserActive`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, isActive }),
    });
    if (!response.ok) throw new Error("Failed to update user");
    return await response.json();
  } catch (error) {
    console.error("Error updating user:", error);
    return null;
  }
};

export const sendPasswordResetEmail = async (email) => {
  try {
    await firebaseSendResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error("Virhe lähetettäessä resettilinkkiä:", error);
    return { success: false, error: error.message };
  }
};

export const createUser = async ({ companyId, firstName, lastName, email, password, roles }) => {
  try {
    const res = await authFetch(`${API_BASE_URL}/createUser`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ companyId, firstName, lastName, email, password, roles }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const code = err?.error || 'create_failed';
      const http = res.status;
      const error = new Error(code);
      error.code = code;
      error.httpStatus = http;
      throw error;
    }
    return await res.json();
  } catch (e) {
    throw e;
  }
};

/* ---------- INVITES ---------- */

export const fetchInvites = async (companyId) => {
  const res = await authFetch(
    `${API_BASE_URL}/fetchInvites?companyId=${encodeURIComponent(companyId)}`,
    { method: "GET" }
  );
  if (!res.ok) throw new Error('fetchInvites failed');
  return await res.json();
};

export const createInvite = async ({ companyId, name, email, roles }) => {
  const res = await authFetch(`${API_BASE_URL}/createInvite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, name, email, roles }),
  });
  if (!res.ok) throw new Error('createInvite failed');
  return await res.json();
};

export const resendInvite = async (inviteId) => {
  const res = await authFetch(`${API_BASE_URL}/resendInvite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inviteId }),
  });
  if (!res.ok) throw new Error('resendInvite failed');
  return await res.json();
};

export const cancelInvite = async (inviteId) => {
  const res = await authFetch(`${API_BASE_URL}/cancelInvite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ inviteId }),
  });
  if (!res.ok) throw new Error('cancelInvite failed');
  return await res.json();
};

/*
 * verifyInvite ja redeemInvite jätetään julkisiksi,
 * koska käyttäjä ei ole vielä kirjautunut sisään siinä vaiheessa.
 */
export const verifyInvite = async (token) => {
  const res = await fetch(`${API_BASE_URL}/verifyInvite?token=${encodeURIComponent(token)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err?.error || 'verify_failed');
    e.code = err?.error;
    throw e;
  }
  return res.json();
};

export const redeemInvite = async ({ token, password, firstName, lastName }) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/redeemInvite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password, firstName, lastName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err?.error || 'redeem_failed');
    e.code = err?.error;
    throw e;
  }
  return res.json();
};

const UsersApi = {
  fetchUsers,
  fetchUserById,
  updateLastSigned,
  uploadProfileImage,
  updateProfileImageUrl,
  updateUserConsent,
  updateUser,
  updateUserRole,
  updateUserRoles,
  updateUserActive,
  sendPasswordResetEmail,
  createUser,
  fetchInvites,
  createInvite,
  resendInvite,
  cancelInvite,
  verifyInvite,
  redeemInvite,
};

export default UsersApi;